/**
 * Console root: query client → auth → role gate → router (docs/06 §1–§2).
 *
 * The gate is deliberately outside the router: an operator without a role never reaches a URL
 * of the console, and the API refuses them anyway (`requireRole`, docs/03 §1).
 */
import type { PublicConfig } from '@akpl/shared';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { useMemo } from 'react';

import { AuthProvider, hasCmsAccess, useAuth } from './auth/AuthProvider';
import type { CmsAuthClient } from './auth/client';
import { Button } from './components/ui/button';
import { ConfirmProvider } from './components/ui/confirm';
import { BootSkeleton } from './components/ui/skeleton';
import { ToastProvider } from './components/ui/toast';
import { useT } from './i18n';
import { type CmsRouter, createCmsRouter } from './router';
import { Login } from './screens/Login';
import { NoAccess } from './screens/NoAccess';

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: { retry: false, refetchOnWindowFocus: false, staleTime: 30_000 },
    },
  });
}

function Gate({ router, supportEmail }: { router: CmsRouter; supportEmail?: string }) {
  const { t } = useT();
  const { status, role, error, reload, signOut } = useAuth();

  /**
   * A cold worker keeps the operator here for up to ~18 s (UX-504). A shaped skeleton of the
   * shell, and a sentence once the wait stops being normal, instead of a bare "Cargando…".
   */
  if (status === 'loading')
    return <BootSkeleton label={t('common:states.loading')} slowLabel={t('cms:boot.slow')} />;
  if (status === 'signed-out') return <Login supportEmail={supportEmail} />;
  if (status === 'error') {
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center gap-3 bg-canvas">
        <p role="alert" className="text-[13px] text-danger">
          {error ?? t('common:states.error')}
        </p>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={reload}>
            {t('common:actions.retry')}
          </Button>
          <Button variant="ghost" onClick={() => void signOut()}>
            {t('common:actions.logout')}
          </Button>
        </div>
      </main>
    );
  }
  if (!hasCmsAccess(role)) return <NoAccess supportEmail={supportEmail} />;
  return <RouterProvider router={router} />;
}

export interface AppProps {
  authClient: CmsAuthClient;
  config?: PublicConfig | null;
  /** Injected by the tests; production builds create one per boot. */
  router?: CmsRouter;
  queryClient?: QueryClient;
}

export function App({ authClient, config, router, queryClient }: AppProps) {
  const activeRouter = useMemo(() => router ?? createCmsRouter(), [router]);
  const client = useMemo(() => queryClient ?? createQueryClient(), [queryClient]);

  return (
    <QueryClientProvider client={client}>
      <AuthProvider client={authClient}>
        {/* Every destructive action in the console asks first (UX-211) and, where it can be
            walked back, offers the walk-back in a toast. Both live above the router. */}
        <ConfirmProvider>
          <ToastProvider>
            <Gate router={activeRouter} supportEmail={config?.supportEmail} />
          </ToastProvider>
        </ConfirmProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
